import { Box, Container, Typography } from '@mui/material';
import calendarIcon from 'assets/images/calendar-icon.svg';
import searchIcon from 'assets/images/search-icon.svg';
import { AxiosError } from 'axios';
import { Loader } from 'components';
import { genral } from 'constants/index';
import { useBranchContext } from 'context/BranchesContext';
import { useUserSearchContext } from 'context/UserSearchContext';
import { differenceInCalendarDays, format } from 'date-fns';
import { useBranchHoursToGetReady } from 'hooks/useBranches';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { validateBranchWithLive } from 'services/bookingService';
import { LocationSectionProps } from 'types';
import { SearchPayload } from 'types/searchPayload';

type BranchTypeProps = 'out' | 'in';
export const Search: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { bookingSearchData, setBookingSearchData } = useUserSearchContext();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { kioskLocation } = useBranchContext();
  let activeBranchId;

  if (bookingSearchData.branchType === 'out') {
    activeBranchId = bookingSearchData.OutDetails.BranchId;
  } else if (bookingSearchData.branchType === 'in') {
    activeBranchId = bookingSearchData.InDetails.BranchId;
  }

  const {
    data: branchHours,
    isLoading: branchHoursIsLoading,
    error: branchHoursError,
  } = useBranchHoursToGetReady(activeBranchId!);

  const LocationSection: React.FC<LocationSectionProps> = ({
    title,
    location,
    onClick,
  }) => {
    return (
      <>
        <Box className="locationTitle">{title}</Box>
        <Box
          className="locationContainer"
          onClick={() => onClick({ fieldName: '', value: '' })}
        >
          <img
            loading="lazy"
            src={location.iconSrc}
            className="locationIcon"
            alt="Location icon"
          />
          <Typography className="locationName">{location.name}</Typography>
        </Box>
      </>
    );
  };

  const navigate = useNavigate();

  const SelectionHandler = (data: {
    fieldName: BranchTypeProps;
    value: string;
  }) => {
    navigate(`/location/${data.fieldName}`);
  };
  const DateSelection = (data: { fieldName: string; value: string }) => {
    navigate(`/date/${data.fieldName}`);
  };
  const TimeSelection = (data: { fieldName: string; value: string }) => {
    navigate(`/time/${data.fieldName}`);
  };

  const searchHandler = async () => {
    const { OutDetails, InDetails } = bookingSearchData;
    setError(null);
    // Early validation
    if (!OutDetails?.BranchId || !InDetails?.BranchId) {
      setError('Please select pickup and return locations');
      return;
    }

    const searchPayload: SearchPayload = {
      OutDetails: {
        BranchId: OutDetails.BranchId,
        Date: OutDetails.Date,
        StateCode: OutDetails.StateCode,
        IsDelOrCol: false,
        Time: OutDetails.Time,
      },
      InDetails: {
        BranchId: InDetails.BranchId,
        Date: InDetails.Date,
        StateCode: InDetails.StateCode,
        IsDelOrCol: false,
        Time: InDetails.Time,
      },
      Daily: bookingSearchData.Daily,
      CountryCode: bookingSearchData.CountryCode,
      Brand: bookingSearchData.Brand,
      IPLocation: { ...bookingSearchData.IPLocation },
      DeviceInfo: bookingSearchData.DeviceInfo,
      Query: window?.location?.href,
    };

    try {
      setLoading(true);
      const { isPassed, data, status } =
        await validateBranchWithLive(searchPayload);

      if (!isPassed) {
        setError(status || 'Validation failed.');
        return;
      }

      if (data?.Id) {
        const {
          Id,
          OutDetails: newOutDetails,
          InDetails: newInDetails,
          Daily,
          CountryCode,
          Brand,
          IPLocation: newIPLocation,
          RentalDays,
        } = data;

        setBookingSearchData((prev) => ({
          ...prev,
          SearchId: Id,
          OutDetails: { ...newOutDetails },
          InDetails: { ...newInDetails },
          Daily,
          CountryCode,
          Brand,
          IPLocation: { ...newIPLocation },
          RentalDays,
        }));

        const locationCode = searchParams.get('locationCode')
          ? `/listing?id=${Id}&locationCode=${searchParams.get('locationCode')}`
          : `/listing?id=${Id}`;
        navigate(`${locationCode}`);
      }
    } catch (err: unknown | any) {
      const axiosError = err as AxiosError<{ status?: string }>;
      if (axiosError.response) {
        setError(
          err.response.data.details.status || 'Validation request failed.'
        );
      } else if (axiosError.request) {
        setError('No response from server.');
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (branchHours?.result) {
      let { OutDate, OutTime, InDate } = branchHours.result;
      const diffDays = differenceInCalendarDays(InDate, OutDate);

      if (
        bookingSearchData.OutDetails.BranchId !==
        bookingSearchData.InDetails.BranchId
      ) {
        setBookingSearchData((prev) => ({
          ...prev,
          InDetails: {
            ...prev.InDetails,
            Date: format(InDate, 'dd MMM yyyy'),
            Time: OutTime,
          },
          RentalDays: diffDays,
        }));
      } else {
        setBookingSearchData((prev) => ({
          ...prev,
          OutDetails: {
            ...prev.OutDetails,
            Date: format(OutDate, 'dd MMM yyyy'),
            Time: OutTime,
          },
          InDetails: {
            ...prev.InDetails,
            Date: format(InDate, 'dd MMM yyyy'),
            Time: OutTime,
          },
          RentalDays: diffDays,
        }));
      }
    }
  }, [branchHours]);

  useEffect(() => {
    if (
      kioskLocation &&
      kioskLocation.Code &&
      (!bookingSearchData.OutDetails.BranchId ||
        !bookingSearchData.InDetails.BranchId)
    ) {
      const baseLocation = {
        BranchId: kioskLocation.Id,
        BranchCode: kioskLocation.Code,
        StateCode: kioskLocation.StateCode,
        IsDelOrCol: false,
        BranchName: kioskLocation.Name,
        AreaName: kioskLocation.Address,
        Address: kioskLocation.Address,
        StateName: kioskLocation.StateName,
      };
      setBookingSearchData((prev) => ({
        ...prev,
        OutDetails: {
          ...baseLocation,
          Date: bookingSearchData.OutDetails.Date,
          Time: bookingSearchData.OutDetails.Time,
        },
        InDetails: {
          ...baseLocation,
          Date: bookingSearchData.InDetails.Date,
          Time: bookingSearchData.InDetails.Time,
        },
        branchType: 'out',
        activeBranchId: baseLocation.BranchId,
      }));
    }
  }, [kioskLocation]);

  useEffect(() => {
    if (error || branchHoursError) {
      toast.error(error || branchHoursError.message);
    }
  }, [error, branchHoursError]);

  if (loading || branchHoursIsLoading) return <Loader />;

  return (
    <Box className="search-page" sx={{ padding: '24px' }}>
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h2" pb={4}>
          {genral.lable_for_search_engine}
        </Typography>

        <Box className="container">
          <LocationSection
            title="Pickup Location"
            onClick={() => SelectionHandler({ fieldName: 'out', value: '' })}
            location={{
              name: bookingSearchData
                ? bookingSearchData.OutDetails.BranchName
                : 'Search Location',
              iconSrc: searchIcon,
            }}
          />

          <LocationSection
            title="Return Location"
            onClick={() => SelectionHandler({ fieldName: 'in', value: '' })}
            location={{
              name: bookingSearchData
                ? bookingSearchData.InDetails.BranchName
                : 'Search Location',
              iconSrc: searchIcon,
            }}
          />
          <Box className="dateTimeTitle">Pickup Date & Time</Box>
          <Box className="dateTimeWrapper">
            <Box
              className="dateContainer"
              onClick={() =>
                DateSelection({ fieldName: 'pickup-date', value: '' })
              }
            >
              <img
                loading="lazy"
                src={calendarIcon}
                className="calendarIcon"
                alt="Calendar icon"
              />
              <Box className="date">{`${bookingSearchData ? bookingSearchData.OutDetails.Date : ''}`}</Box>
            </Box>
            <Box
              sx={{ borderLeft: '0px' }}
              className="time"
              onClick={() =>
                TimeSelection({ fieldName: 'pickup-time', value: '' })
              }
            >
              {`${bookingSearchData ? bookingSearchData.OutDetails.Time : ''}`}
            </Box>
          </Box>
          <Box className="dateTimeTitle">Return Date & Time</Box>
          <Box className="dateTimeWrapper">
            <Box
              className="dateContainer"
              onClick={() =>
                DateSelection({ fieldName: 'return-date', value: '' })
              }
            >
              <img
                loading="lazy"
                src={calendarIcon}
                className="calendarIcon"
                alt="Calendar icon"
              />
              <Box className="date">
                {`${bookingSearchData && bookingSearchData.InDetails.Date}`}
              </Box>
            </Box>
            <Box
              sx={{ borderLeft: '0px' }}
              className="time"
              onClick={() =>
                TimeSelection({ fieldName: 'return-time', value: '' })
              }
            >
              {`${bookingSearchData && bookingSearchData.InDetails.Time}`}
            </Box>
          </Box>
          <button
            className="searchButton"
            onClick={() => bookingSearchData && searchHandler()}
            aria-label="Search for available cars"
          >
            SEARCH CAR
          </button>
        </Box>
      </Container>
    </Box>
  );
};
